import { buildBeforeTrack } from './buildBeforeTrack';
import { setClient } from './client';
import type { AnalyticsConfig } from './types';

/**
 * Element attributes autocapture records by default, independently of
 * `capture_text_content`. UPMS puts customer data in all four — business names,
 * policy numbers and document names all reach the DOM through `aria-label` — so
 * they are blocked at the SDK boundary instead of being scrubbed per component.
 *
 * `href`, `name`, `role` and `type` stay captured: they carry no customer data
 * here, and `href` is what makes outbound links distinguishable in reporting.
 */
const BLOCKED_ATTRIBUTES = ['aria-label', 'aria-labelledby', 'aria-describedby', 'title'];

let started = false;

/**
 * Starts autocapture. Safe to call before a project token exists — without one
 * this does nothing at all and the SDK is never downloaded.
 *
 * Returns immediately; the SDK loads in the background. That defers the first
 * page view of a session by the time it takes to fetch the chunk, so a visitor
 * who leaves within a few hundred milliseconds goes uncounted. The trade is
 * deliberate: a static import ships ~120 KB gzipped to every visitor, including
 * the environments that have no token at all.
 */
export const initAnalytics = (config: AnalyticsConfig) => {
  if (!config.isBrowser || started) return;

  // Mixpanel's own `init` silently no-ops without a token, but every later call
  // on the half-built instance throws. Bail before one can be made.
  if (config.token === '') return;

  // Claimed before awaiting the import so two calls in the same tick cannot both
  // load the SDK.
  started = true;

  const client = import('mixpanel-browser')
    .then(({ default: mixpanel }) => {
      mixpanel.init(config.token, {
        autocapture: {
          // Path only: search params drive pagination and filters across UPMS, and
          // 'full-url' would emit a page view every time one changed.
          pageview: 'url-with-path',
          click: true,
          dead_click: true,
          input: true,
          rage_click: true,
          scroll: true,
          submit: true,
          capture_text_content: false,
          block_attrs: BLOCKED_ATTRIBUTES,
        },
        // Cookie persistence is cross-subdomain by default, which would hand the
        // analytics identifier to every other app on the shared cookie domain.
        persistence: 'localStorage',
        // No analytics for anonymous visitors: every tracking call — including
        // the autocaptured pageview `init` fires synchronously — is silently
        // ignored until `optIn` runs post sign-in.
        opt_out_tracking_by_default: true,
        debug: config.debug ?? false,
        // Mixpanel's own internal runtime errors (autocapture listener
        // failures, invalid track() calls, storage errors) otherwise only
        // reach console.error and are lost in production.
        error_reporter: (msg, err) => {
          config.onError?.(err ?? msg);
        },
        hooks: {
          before_track: buildBeforeTrack({
            app: config.app,
            environment: config.environment,
            getPathname: config.getPathname,
          }),
        },
      });

      return mixpanel;
    })
    .catch((error: unknown) => {
      // Swallow a failed chunk load or initialisation so an analytics outage
      // never breaks the calling app.
      config.onError?.(error);
      return undefined;
    });

  setClient(client);
};
